<template>
  <div>
    <KTCard ref="preview" v-bind:title="'Add new'">
      <template v-slot:title>
        <h3 class="card-label">
          <router-link :to="{ name: 'list-ai-invoice-accounting-value-types' }">
            {{ $t("MENU.ITEM.EXP.AI_INVOICE_ACCOUNTING_VALUE_TYPES") }}
          </router-link>
          <i class="mr-2"></i>
          <small class="">{{ $t("LABELS.ADD_NEW") }}</small>
        </h3>
      </template>
      <template v-slot:body>
        <b-form>
          <FormBuilder
            :form="form"
            :schemaJson="schemaJson"
            :v="$v"
          ></FormBuilder>
        </b-form>
      </template>

      <template v-slot:foot>
        <div
          v-show="_.size(errors) > 0"
          role="alert"
          class="alert alert-danger"
        >
          <div class="alert-text" v-for="(error, i) in errors" :key="i">
            {{ error }}
          </div>
        </div>
      </template>
      <template v-slot:toolbarfoot>
        <div class="example-tools justify-content-center">
          <BackButton></BackButton>
          <SaveButtonDropdown
            :defaultAction="'continue'"
            :isLoading="isLoadingAiInvoiceAccountingValueType"
            v-on:save="onSave($event)"
          ></SaveButtonDropdown>
        </div>
      </template>
    </KTCard>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { SAVE_AI_INVOICE_ACCOUNTING_VALUE_TYPE } from "@/modules/ai-Invoice-accounting-value-types/store/ai-invoice-accounting-value-types.module";
import FormBuilder from "@/view/content/forms/FormBuilder.vue";
import SaveButtonDropdown from "@/view/content/forms/components/SaveButtonDropdown.vue";
import { validationMixin } from "vuelidate";
import { formBuilderMixin } from "@/core/mixins/formBuilderMixin.js";
import KTCard from "@/view/content/Card.vue";
import { mapGetters, mapState } from "vuex";
import { permissionMixin } from "@/core/mixins/permissionMixin.js";
import { FETCH_AI_INVOICE_TYPES } from "@/modules/ai-invoices/store/ai-invoices.module";

export default {
  mixins: [validationMixin, formBuilderMixin, permissionMixin],
  data() {
    return {
      errors: {},
      invoiceTypes: [],
      schemaJson: {
        fields: [
          {
            type: "input",
            inputType: "text",
            id: "name-input",
            groupId: "name-group",
            required: "required",
            feedback_id: "name-live-feedback",
            divider: false,
            i18n: {
              label: "FORM_LABELS.NAME",
              placeholder: "FORM_PLACEHOLDERS.NAME",
              validation: "FORM.VALID_NAME"
            },
            validations: {
              required: true,
              minLength: 2
            },
            translatable: false,
            model: "name"
          },
          {
            type: "input",
            inputType: "text",
            id: "code-input",
            groupId: "code-group",
            required: "required",
            feedback_id: "code-live-feedback",
            divider: false,
            i18n: {
              label: "FORM_LABELS.CODE",
              placeholder: "FORM_PLACEHOLDERS.CODE",
              validation: "FORM.VALID_CODE"
            },
            validations: {
              required: true,
              minLength: 1
            },
            translatable: false,
            model: "code"
          },
          {
            type: "select",
            id: "ai-invoice-type-select",
            groupId: "ai-invoice-type-group",
            required: "required",
            feedback_id: "ai-invoice-type-live-feedback",
            divider: false,
            i18n: {
              label: "MENU.ITEM.EXP.AI_INVOICE_TYPE",
              placeholder: "FORM_PLACEHOLDERS.SELECT_INVOICE_TYPE",
              validation: "FORM.VALID_INVOICE_TYPE"
            },
            validations: {
              required: true
            },
            translatable: false,
            model: "aiInvoiceTypeIds",
            options: [],
            multiple: true
          }
        ]
      }
    };
  },
  components: {
    KTCard,
    FormBuilder,
    SaveButtonDropdown
  },
  mounted() {
    let vm = this;
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("MENU.ITEM.EXP.AI_INVOICE_ACCOUNTING_VALUE_TYPES"),
        route: { name: "list-ai-invoice-accounting-value-types" }
      },
      { title: this.$i18n.t("MENU.ITEM.ADD") }
    ]);

    this.$store
      .dispatch(FETCH_AI_INVOICE_TYPES)
      .then(data => {
        vm.invoiceTypes = Array.isArray(data) ? data : data.data || [];
        const typeField = vm.schemaJson.fields.find(
          f => f.model === "aiInvoiceTypeIds"
        );
        if (typeField && vm.invoiceTypes.length > 0) {
          vm.$set(
            typeField,
            "options",
            vm.invoiceTypes.map(t => ({
              id: t.id,
              name: t.name
            }))
          );
        }
      })
      .catch(error => {
        console.error("Failed to fetch invoice types:", error);
      });

    vm.generateFormOutOfSchemaJson(this.schemaJson);
  },
  computed: {
    ...mapGetters(["isLoadingAiInvoiceAccountingValueType"]),
    ...mapState({
      errors: state => state.auth.errors
    })
  },
  validations() {
    let vm = this;
    let tmpValidationObject = this.generateFormValidationsOutOfSchemaJson(
      this.schemaJson
    );
    return tmpValidationObject;
  },
  methods: {
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    onSubmit(evt) {
      evt.preventDefault();
    },
    onSave(next) {
      let vm = this;
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        vm.$notify({
          group: "notify",
          type: "error",
          title: "<i class='flaticon-close'></i> Warning",
          text: "Please fill all required fields correctly"
        });
        return;
      }

      const payload = {
        name: this.form.name,
        code: this.form.code,
        aiInvoiceTypeIds: Array.isArray(this.form.aiInvoiceTypeIds)
          ? this.form.aiInvoiceTypeIds
          : this.form.aiInvoiceTypeIds
          ? [this.form.aiInvoiceTypeIds]
          : []
      };

      this.$store
        .dispatch(SAVE_AI_INVOICE_ACCOUNTING_VALUE_TYPE, payload)
        .then(data => {
          vm.errors = data.data.errors;
          let itemEdit = data.data;
          vm.$notify({
            group: "notify",
            type: "success",
            title: "<i class='flaticon2-checkmark'></i> Success",
            text: data.message
          });

          if (next == "continue") {
            this.$router.push({
              name: "edit-ai-invoice-accounting-value-types",
              params: { id: itemEdit.id }
            });
          }
          if (next == "new") {
            vm.resetVForm();
            vm.generateFormOutOfSchemaJson(this.schemaJson);
          }
          if (next == "exit") {
            this.$router.push({
              name: "list-ai-invoice-accounting-value-types"
            });
          }
        })
        .catch(response => {
          vm.errors = response.data.errors;
          vm.$notify({
            group: "notify",
            type: "error",
            title: this.$t("ALERTS.ERROR"),
            text: response.data.message
          });
        });
    },
    resetVForm() {
      this.$v.$reset();
    }
  }
};
</script>
